import * as React from 'react';
import {classes, style} from 'typestyle';
import {
  betweenJustified,
  center,
  centerCenter,
  centerJustified,
  end,
  endJustified,
  flex1,
  horizontal,
  horizontallySpaced,
  vertical,
  verticallySpaced
} from 'csstips';
import {important, percent, px, rem, url} from 'csx';
import {SVGIcon} from './svg-icon';
import {ButtonCircle} from './button-circle';
import {colorBackground, colorBrand, colorSlate, colorSubtle} from '../theme/color';
import {ButtonPill} from './button-pill';
import {observer} from 'mobx-react';
import {mediaMobileOnly, mediaTablet} from '../theme/media';
import {Alert} from './alert';
import {transitionQuickEase} from '../theme/transition';
import {ArtistLinkList} from './artist-link-list';
import {resetAnchorStyles} from '../theme/reset';
import {ChannelSchema, LicenceSchema, TrackSchema} from '../types/schema';
import {getTrackImageURL} from '../lib/image';
import {ImagePreloader} from '../types/fn';
import {formatCurrency} from '../lib/currency';
import {WaveformImage} from './waveform-image';
import {ConnectedTrackVouchStatus} from './track/vouch-status';

export interface PageCheckoutTracksModel {
  tracks: Array<TrackSchema>;
  licences: Array<LicenceSchema>;
  currency: string;
  price: string | number;
  priceBrandSponsored: string | number;
  prices: Array<string | number>;
  brandedContentWarning: string;
  channel: ChannelSchema;
  step: number;
}

export interface PageCheckoutTracksProps {
  preload: ImagePreloader;
  preloadWaveform: ImagePreloader;
  className?: string;
  model: PageCheckoutTracksModel;
  getPrice?(identity: string): number;

  onRemoveTrack?(identity: string): void;
  onClickLink(event: React.MouseEvent<any>): void;

  onDownload?(licenceId: string): void;
  onAttribution?(licenceId: string): void;

  collapsed: boolean;
  download?: boolean;
  PlayButton: any;
  BrandSponsoredLabel: any;
  i18n?: {
    title: string;
    remove: string;
    rights: string;
    download: string;
    attribution: string;
    eula: string;
    labelEula: string;
    standardEula: string;
    empty: string;
  },
  onLabelCopyrightClick?: any;
}

export interface PageCheckoutTracksState {
  limitations: { [identity: string]: boolean }
}

@observer
export class PageCheckoutTracks extends React.Component<PageCheckoutTracksProps, PageCheckoutTracksState> {
  private container: HTMLDivElement;

  state = { limitations: {} };

  render() {
    const { preload, preloadWaveform,
      className, model, PlayButton, i18n = PageCheckoutTracks.i18n, onRemoveTrack, download, onDownload, onClickLink, BrandSponsoredLabel, onLabelCopyrightClick } = this.props;

    if (model.tracks.length === 0) return (
      <div className={PageCheckoutTracks.styles.empty}>
        {i18n.empty}
      </div>
    );

    let seenFirstStandardLicense = false;

    return (
      <div className={className} ref={el => this.container = el}>
        {model.tracks.map((track, index) => {
          const price = model.channel
            ? typeof model.prices[index] !== 'undefined'
              ? model.prices[index]
              : model.price
            : track.basket.brand_sponsored
              ? model.priceBrandSponsored
              : model.price;

          let isFirstLicenseFree = false;

          if (!model.channel && !seenFirstStandardLicense && !track.basket.brand_sponsored)
            seenFirstStandardLicense = isFirstLicenseFree = true;

          return (
            <div key={index} className={PageCheckoutTracks.styles.track}>
              <div className={PageCheckoutTracks.styles.tile}
                   style={{ backgroundImage: url(getTrackImageURL(track, preload)) }}>
                <PlayButton track={track}/>
              </div>
              <div className={PageCheckoutTracks.styles.details}>
                <div className={PageCheckoutTracks.styles.header}>
                  <a
                    className={PageCheckoutTracks.styles.link}
                    href={`/music/artists/${track.artists[0].slug}/track/${track.slug}`}
                    onClick={onClickLink}
                  >{track.title}</a>
                  {track.basket && track.basket.brand_sponsored && (
                    <BrandSponsoredLabel expanded={true}/>
                  )}
                </div>
                <ArtistLinkList
                  model={{ artists: track.artists }}
                  onClickLink={onClickLink}
                  className={PageCheckoutTracks.styles.linkArtist}
                  linkClassName={PageCheckoutTracks.styles.linkArtistItem}
                />
                <div className={PageCheckoutTracks.styles.waveformContainer}>
                  <WaveformImage
                    className={PageCheckoutTracks.styles.waveform}
                    identity={track.audio.identity}
                    preloadWaveform={preloadWaveform}
                  />
                  <div
                    className={PageCheckoutTracks.styles.price}>
                    {!model.channel && (
                      <div>from</div>
                    )}
                    {isFirstLicenseFree && (
                      <span>{formatCurrency(price, model.currency)}</span>
                    )}
                    {formatCurrency(isFirstLicenseFree ? 0 : price, model.currency)}
                  </div>
                </div>
                <div className={PageCheckoutTracks.styles.buttonsContainer}>
                  {onRemoveTrack && (
                    <div className={PageCheckoutTracks.styles.buttons} onClick={() => onRemoveTrack(track.identity)}>
                      <ButtonCircle size={30} className={PageCheckoutTracks.styles.button}>
                        <SVGIcon.Cross size={10}/>
                      </ButtonCircle>
                      <span>{i18n.remove}</span>
                    </div>
                  )}
                  {download && (
                    <div
                      className={PageCheckoutTracks.styles.downloadButtons}>
                      <ButtonPill
                        className={PageCheckoutTracks.styles.actionButton}
                        muted={true}
                        href={`/account/licenses`}
                        onClick={onClickLink}
                        children={i18n.eula}
                      />
                      {onDownload && (
                        <ButtonPill
                          className={PageCheckoutTracks.styles.actionButton}
                          primary={true}
                          onClick={onDownload.bind(null, this.getLicenceIdentity(track))}
                          children={i18n.download}
                        />
                      )}
                    </div>
                  )}
                  {!download && track.branded_content_allowed === 0 && (
                    <div className={classes(PageCheckoutTracks.styles.buttons, this.maybeHide())}>
                      <span>{i18n.rights}</span>
                      <ButtonCircle onClick={this.toggleLimitations.bind(this, track.identity)} size={30}
                                    className={classes(PageCheckoutTracks.styles.button, track.branded_content_allowed === 0 && PageCheckoutTracks.styles.buttonActive)}>
                        <SVGIcon.ArrowRight size={10}
                                            className={classes(PageCheckoutTracks.styles.chevron, this.state.limitations[ track.identity ] && PageCheckoutTracks.styles.chevronActive)}/>
                      </ButtonCircle>
                    </div>
                  )}
                  {!download && track.branded_content_allowed !== 0 && (
                    <div className={classes(PageCheckoutTracks.styles.buttons, this.maybeHide())}/>
                  )}
                  {model.step == 0 && (
                      <div className={style({display: "flex", justifyContent: "flex-end", lineHeight: "0.8"})}>
                      {track.reporting_party && track.reporting_party.slug === 'umg' && (
                          <span onClick={onLabelCopyrightClick}>
                            <SVGIcon.Copyright size={30} className={style({marginRight:"10px", cursor:"pointer"})} />
                          </span>
                      )}
                      <ButtonPill
                          className={PageCheckoutTracks.styles.licenseButton}
                          muted={true}
                          href={process.env.ASSET_URL + '/eula/' + (track.reporting_party && track.reporting_party.slug === 'umg' ? 'label' : 'lickd') + '_eula.html'}
                          target={'_blank'}
                          children={track.reporting_party && track.reporting_party.slug === 'umg' ? i18n.labelEula : i18n.standardEula}
                      />
                      </div>
                  )}
                </div>
                {!download && this.state.limitations[ track.identity ] && track.branded_content_allowed === 0 && (
                  <Alert type="simple"
                         title="Sponsored content not allowed"
                         onClose={this.toggleLimitations.bind(this, track.identity)}
                         position="top-right">
                    <p className={PageCheckoutTracks.styles.alertParagraph}>{model.brandedContentWarning}</p>
                  </Alert>
                )}
                <ConnectedTrackVouchStatus identity={track.identity} />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  componentWillMount() {
    this.prepareLimitations(this.props)
  }

  componentWillReceiveProps(nextProps) {
    this.prepareLimitations(nextProps);
  }

  prepareLimitations = (props: PageCheckoutTracksProps) => {
    const limitations = props.model.tracks.reduce((acc, track) => ({
      ...acc,
      [track.identity]: track.branded_content_allowed === 0
    }), {});

    this.setState({ limitations });
  };

  getLicenceIdentity = (track: TrackSchema) => {
    const licence = this.props.model.licences.find(licence => licence.track.identity === track.identity);
    return licence ? licence.identity : null
  };

  maybeHide = () => this.props.collapsed && PageCheckoutTracks.styles.hide;

  toggleLimitations = (trackIdentity: string) => {
    this.setState({
      limitations: {
        ...this.state.limitations,
        [trackIdentity]: !this.state.limitations[ trackIdentity ]
      }
    });
  };

  static i18n = {
    title: 'Your tracks',
    remove: 'Remove',
    rights: 'Restrictions',
    download: 'Download track',
    eula: 'View Licences',
    labelEula: 'View label license',
    standardEula: 'View standard license',
    attribution: 'View video attribution',
    empty: 'Your basket is empty',
  };

  static styles = {
    empty: style({
      ...centerCenter,
      height: px(120),
      fontWeight: 200,
      fontSize: px(18),
      color: colorSubtle.toString()
    }),
    header: style({
      ...horizontal,
      ...betweenJustified,
    }),
    link: style(
      {
        color: important(colorSlate.toString()),
        flex: 1,
        textDecoration: 'underline',
        fontWeight: 700,
        fontSize: px(16),
        lineHeight: px(21),
        whiteSpace: 'pre',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        $nest: { ...resetAnchorStyles(colorBrand.toString()) }
      },
      mediaMobileOnly({
        padding: '0 0 0 65px'
      })
    ),
    linkArtist: style(
      {
        fontWeight: 400,
        fontSize: px(14),
        lineHeight: px(21),
        whiteSpace: 'pre',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
      },
      mediaMobileOnly({
        padding: '0 0 0 65px'
      })
    ),
    linkArtistItem: style({
      fontSize: px(12),
      color: important(colorSubtle.toString()),
      $nest: { ...resetAnchorStyles(colorBrand.toString()) }
    }),
    track: style({
      ...horizontal,
      ...horizontallySpaced(15),
      position: 'relative',
      width: percent(100),
      borderBottom: `1px solid ${colorBackground.fade(0.7).toString()}`,
      padding: '15px 0',
      $nest: {
        '&:last-child': {
          borderBottom: 'none',
        }
      }
    }),
    tile: style(
      {
        ...horizontal,
        ...endJustified,
        ...end,
        flexShrink: 0,
        width: px(150),
        height: px(150),
        padding: px(5),
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      },
      mediaMobileOnly({
        ...centerCenter,
        position: 'absolute',
        top: px(15),
        left: 0,
        width: px(50),
        height: px(50)
      })
    ),
    hide: style({
      display: important('none')
    }),
    details: style({
      ...flex1,
      ...vertical,
      padding: '5px 0',
      overflow: 'hidden'
    }),
    waveformContainer: style({
      ...flex1,
      ...horizontal
    }),
    waveform: style(
      {
        ...flex1,
        borderRadius: px(50),
        minHeight: px(48),
        maxHeight: px(48),
        margin: '10px 0',
        backgroundSize: '100% 100%',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      },
      mediaMobileOnly({
        minHeight: px(40)
      })
    ),
    price: style({
      ...vertical,
      ...centerJustified,
      ...end,
      position: 'relative',
      width: px(140),
      fontSize: px(30),
      $nest: {
        '& div': {
          display: 'block',
          fontSize: rem(0.75),
          height: rem(1.5),
          marginTop: rem(-1.25),
          color: colorSubtle.toString()
        },
        '& span': {
          display: 'block',
          textDecoration: 'line-through',
          fontSize: rem(1),
          height: rem(1.5),
        }
      }
    }),
    buttonsContainer: style({
      ...horizontal,
      ...betweenJustified,
      minHeight: px(30),
      $nest: {
        '&> div:last-child': {
          ...flex1,
          textAlign: 'right'
        }
      }
    }),
    buttons: style({
      ...horizontal,
      ...horizontallySpaced(15),
      ...endJustified,
      ...center
    }),
    button: style({
      backgroundColor: important(colorBackground.toString()),
      transition: `background-color ${transitionQuickEase}`,
      $nest: {
        '&:hover': {
          backgroundColor: important(colorBackground.darken(0.15).toString()),
        },
        '& g': {
          stroke: colorBrand.toString(),
        }
      }
    }),
    buttonActive: style({
      backgroundColor: important(colorBackground.toString()),
      $nest: {
        '& g': {
          stroke: colorBrand.toString(),
        },
      }
    }),
    downloadButtons: style(
      mediaTablet({
        ...horizontal,
        ...horizontallySpaced(15),
        ...endJustified,
        ...center
      }),
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(15)
      })
    ),
    actionButton: style(mediaMobileOnly({
      ...vertical,
      ...verticallySpaced(15)
    })),
    licenseButton: style(mediaMobileOnly({
      ...vertical,
      ...verticallySpaced(15)
    }), {
      width: '180px'
    }),
    chevron: style({
      transform: 'rotate(-90deg)'
    }),
    chevronActive: style({
      transform: important('rotate(90deg)')
    }),
    shareURL: style({
      $nest: {
        '& > p': {
          margin: 0
        }
      }
    }),
    alertParagraph: style({
        margin: 0,
      }
    ),
    labelLicense: style(
        {
          ...centerCenter,
          display: 'inline-flex',
          position: 'relative',
          textAlign: 'left',
        }
    ),
  }
}